/* Linear plasmid chart */
import {
    isSlotEditable,
    standardColoring,
    validationColoring,
    getSlotContent,
    getSlotTitle
} from "../utils/plasmid";

const sizeToValue = {
    small: 1,
    medium: 2,
    large: 3,
}

export default class LinearPlasmid {
    constructor(container) {
        this.container = container;
        this.dataUrl = "";
        this.action = "";
        this.modalContent = false;
        this.showDnaSeq = false;
        this.currentSlot = -1;
        this.zoom = .65;
        this.validationResults = '';
        this.slotSelectable = false;
        this.slotManageable = false;
        this.rulerCircular = false;

        this.prefixUrl = "";
        this.toggleModal = false;

        this.needsSelectable = false;
        this.needsManageable = false;
        this.options = "";
    }

    isEditable(value) {
        if (value !== null && value !== undefined) {
            this.editable = value;
        }
    }

    isSelectable(value) {
        if (value !== null && value !== undefined) {
            this.slotSelectable = value;
        }
    }

    isManageable(value) {
        if (value !== null && value !== undefined) {
            this.slotManageable = value;
        }
    }

    isCircular(value) {
        if (value !== null && value !== undefined && value) {
            this.rulerCircular = value;
        }
    }

    current(value) {
        if (value !== null && value !== undefined)
            this.currentSlot = value ? parseInt(value) : undefined;
    }

    severities(value) {
        if (value !== null && value !== undefined && value) {
            this.validationResults = JSON.parse(value);
        }
    }

    setDataUrl(value) {
        if (value) {
            this.dataUrl = value;
        }
    }

    setAction(value) {
        if (value) {
            this.action = value;
        }
    }

    enableModalContent(value) {
        this.modalContent = value;
    }

    showDnaSequence(value) {
        if (value) {
            this.showDnaSeq = value;
        }
    }

    slotOptions(value) {
        this.options = value || {};
    }

    preset() {
        this.step = 10 * this.zoom;
        this.arrowOffset = 12 * this.zoom;
        this.slotHeight = 60 * this.zoom;
        this.containerHeight = this.slotHeight + 6;
        this.offsetTop = 3;

        if (this.editable) {
            this.step = 15 * this.zoom;
            this.offsetTop = this.slotHeight * 2;
            this.containerHeight = (this.slotHeight * 4) + 6;
        }

        if (this.rulerCircular) {
            this.containerHeight += 30;
        }
    }

    initCartoon(container) {
        this.preset();

        this.area = d3.select(container)
            .append("div")
            .attr("class", "slices overflow-hidden")
            .append("svg")
            .attr("height", this.containerHeight);

        this.ruler = this.area.append("g")
            .attr("transform", "translate(0,0)");

        this.slotsContent = this.area.append("g");
    }

    setData(data) {
        this.slots = [];

        data.slots.forEach((s) => {
            this.slots.push(s);
        });
        this.initCartoon(this.container);
    }

    render() {
        this.contentLength = this.container.parentNode.offsetWidth;

        this.drawSlots();
        this.drawRuler();

        if (this.editable) {
            this.handlers();
        }

        const $areaView = this.area.node();
        $areaView.setAttribute('viewBox', `0 0 ${this.contentLength} 50`)
        $areaView.setAttribute('width', '100%')
        $areaView.setAttribute('height', '100%')
    }

    slotWidth(size) {
        const initWith = 25 * this.zoom;
        return sizeToValue[size] * initWith;
    }

    drawRuler() {

        const positionRule = this.offsetTop + (this.slotHeight / 2);

        let pathRuler;
        if (!this.rulerCircular) {
            pathRuler = `M0,0 L${this.contentLength},0`
        } else {
            const offset = (3 * this.zoom);
            const heightRuler = (this.slotHeight / 2) + offset;

            pathRuler = `M${this.step},0 
                L${this.contentLength - this.step},0
                A${this.step},${this.step} 0 0 1 ${this.contentLength - offset},${this.step} 
                L${this.contentLength - offset},${heightRuler - this.step}
                A${this.step},${this.step} 0 0 1 ${this.contentLength - this.step},${heightRuler}
                L${this.step},${heightRuler}
                A${this.step},${this.step} 0 0 1 ${offset},${heightRuler - this.step}
                L${offset},${this.step}
                A${this.step},${this.step} 0 0 1 ${this.step},0
                `
        }
        this.ruler.append("g")
            .attr("class", "lines")
            .attr("transform", `translate(0, ${positionRule})`)
            .append("path")
            .attr("class", this.slots.length === 0 ? 'stroke-primary-light' : 'ruler')
            .attr("d", pathRuler)
            .style("fill", "none")
            .style("stroke-width", 5 * this.zoom)
            .attr("opacity", 1);
    }

    drawSlots() {
        let end = this.step;

        this.slots.forEach(s => {
            this.drawSlot(s, end);
            end += this.slotWidth(s.size) + this.step;
        });

        if (end > this.contentLength) {
            this.contentLength = end;
        } else {
            this.slotsContent
                .attr("transform", "translate(" + ((this.contentLength - end) / 2) + ",0)")
        }

        this.area.attr("width", this.contentLength);
    }

    drawSlot(slot, end) {
        let left = 0;
        let arrowLeft = 0;
        let right = this.slotWidth(slot.size);
        let arrowRight = this.slotWidth(slot.size);
        let areaShape = this.slotsContent.append("g")
            .attr("transform", `translate(${end}, ${this.offsetTop})`);

        const toggleModal = this.options?.toggleModal

        if (slot.shape === "arrow" && slot.direction) {
            if (slot.direction === 1) {
                right -= this.arrowOffset
            } else {
                left += this.arrowOffset
            }
        }

        let popperContent = getSlotContent(slot, this.showDnaSeq);
        let popperTitle = getSlotTitle(slot);

        areaShape.append("line")
            .attr("x1", 0).attr("y1", 0)
            .attr("x2", arrowRight).attr("y2", 0)
            .attr("data-toggle", "virtual-popper")
            .attr("data-virtual-popper-title", popperTitle)
            .attr("data-virtual-popper-content", popperContent)
            .style("pointer-events", "none")
            .attr("stroke-width", "5");

        areaShape
            .append('a')
            .attr(toggleModal ? 'hx-get' : 'xlink:href', () => {
                if (this.options.notClickable) {
                    return
                }
                if (isSlotEditable(slot, this.options)) {
                    if (this.options.prefixUrl && this.options.action)
                        return this.options.prefixUrl + 'slots/' + slot.id + '/' + this.options.action;
                }
                return slot.urls.view
            })
            .attr('data-bs-toggle',  () => {
                if (isSlotEditable(slot, this.options))
                    return toggleModal && !this.options?.notClickable ? 'modal' : null
            })
            .attr('data-bs-target', (this.modalContent) ? '#modal' : null)
            .attr('hx-target', (this.modalContent) ? '#modal' : null)
            .attr('hx-trigger', (this.modalContent) ? 'click' : null)
            .attr("data-toggle", "virtual-popper")
            .attr("data-virtual-popper-title", popperTitle)
            .attr("data-virtual-popper-content", popperContent)
            .append("path")
            .attr("d", `M${left},0
                L${right},0
                L${arrowRight},${this.slotHeight / 2}
                L${right},${this.slotHeight}
                L${left},${this.slotHeight}
                L${arrowLeft},${this.slotHeight / 2}
                Z`)
            .attr("class", () => {
                // TODO this can be simplified using plasmid.isSlotEditable
                if ((this.slotSelectable && !this.enableClick(this.slotSelectable, slot.selectable)) || (this.slotManageable && !this.enableClick(this.slotManageable, slot.manageable))) {
                    return "readonly";
                } else {
                    if (this.validationResults)
                        return validationColoring(slot, this.validationResults);
                    return standardColoring(slot, undefined, this.currentSlot);
                }
            });
    }

    enableClick(field, value) {
        if (field && value === true) {
            return value;
        }
        return false
    }

    handlers() {
        let positionXAdd = this.step;
        let positionXRemove = this.step;

        const Messages = {
            DELETE_SLOT: 'Delete Slot',
            CREATE_SLOT: 'Create Slot After',
        };

        const labelHandler = (d) => {
            let label = d.title;
            if (d.content) {
                return `<b>${label}</b> <br> ${d.content}`
            } else {
                return `<b>${label}</b> <br> Empty`
            }
        };

        const handlerAdd = this.slotsContent.selectAll(".line-labels")
            .data(this.slots)
            .enter()
            .append("g")
            .attr("class", "line-labels linear")
            .attr("transform", (d) => {
                positionXAdd += this.slotWidth(d.size) + this.step;
                return `translate(${positionXAdd - this.step / 2}, ${this.offsetTop})`
            });

        handlerAdd.append("line")
            .attr("class", "line-links")
            .attr("x1", -5).attr("y1", -(this.slotHeight + 5))
            .attr("x2", 5).attr("y2", -(this.slotHeight + 5))
            .style("pointer-events", "none")
            .attr("stroke-width", "5");

        handlerAdd.append("line")
            .attr("class", "line-links")
            .attr("x1", 0).attr("y1", this.slotHeight)
            .attr("x2", 0).attr("y2", -this.slotHeight);

        handlerAdd.append("text")
            .attr("dy", -(this.slotHeight + 5))
            .attr('data-slot-action', 'create')
            .attr('data-toggle', 'virtual-popper')
            .attr('data-virtual-popper-content', 'Add placeholder')
            .style("text-anchor", "middle")
            .text('+');

        const handlerRemove = this.slotsContent.selectAll(".slice-labels")
            .data(this.slots)
            .enter()
            .append("g")
            .attr("class", "slice-labels")
            .attr("transform", (d) => {
                positionXRemove += this.slotWidth(d.size) + this.step;
                return `translate(${positionXRemove - (this.step + this.slotWidth(d.size) / 2)}, ${this.offsetTop})`
            });

        handlerRemove.append("line")
            .attr("class", "line-links linear")
            .attr("x1", -5).attr("y1", -20)
            .attr("x2", 5).attr("y2", -20)
            .style("pointer-events", "none")
            .attr("stroke-width", "5");

        handlerRemove.append("text")
            .attr("dy", -5)
            .attr('data-slot-action', 'delete')
            .attr('data-toggle', 'virtual-popper')
            .attr('data-virtual-popper-content', function (d) {
                return `Remove ${d.title}`
            })
            .style("text-anchor", "middle")
            .text('-');
    }
}